/**
 * Public types for the market-creation orchestrator. Both web and React
 * Native consume these from the SDK; UI rendering layers (dialog
 * progress strip, mobile equivalent) read `CreateMarketProgressState`
 * verbatim and update on every emit.
 */

export interface MarketEditorialCreateOutcomeVars {
  readonly outcomeIndex: number;
  readonly label: string;
  readonly ticker: string;
  readonly imageUrl: string;
  readonly outcomeMintBase58: string;
}

export interface MarketEditorialCreateVars {
  readonly title: string;
  readonly description: string;
  readonly category: string;
  readonly location: string;
  readonly ticker: string;
  readonly coverImageUrl: string;
  readonly outcomes: ReadonlyArray<MarketEditorialCreateOutcomeVars>;
  readonly creatorPubkey: string;
  readonly marketNonceDecimal: string;
}

export interface CreateMarketOutcomeParams {
  /** Metaplex token name: 1–32 UTF-8 bytes without controls or emoji. */
  readonly name: string;
  /** Metaplex token symbol: 1–10 UTF-8 bytes of uppercase Unicode letters, marks, or numbers without emoji. */
  readonly symbol: string;
  /** Per-outcome ticker. */
  readonly ticker: string;
  /** Metadata URI for the outcome token (IPFS pin recommended). */
  readonly uri: string;
}

interface CreateMarketParamsBase {
  /** Net D6 backing paid by the creator; omitted means no initial backing. */
  readonly initialBackingD6?: string;
  /** Unicode editorial market ticker, validated by the API byte budget. */
  readonly ticker: string;
  /** Metadata URI for the market (IPFS pin recommended). */
  readonly marketUri: string;
  /** One entry per outcome — `outcomes.length` becomes `n_outcomes`. */
  readonly outcomes: ReadonlyArray<CreateMarketOutcomeParams>;
  /** Quote mint pubkey (e.g. devnet USDC) — required for the BC quote vault init. */
  readonly quoteMintBase58: string;
  /** PlatformConfig-derived `market_config` singleton for the active network. */
  readonly marketConfigBase58: string;
}

export interface CreateMarketParamsWithoutEditorial extends CreateMarketParamsBase {
  /**
   * Optional pre-chosen market nonce (u64 decimal string). When omitted
   * the orchestrator picks a fresh random nonce. Required for resume
   * scenarios where the caller needs a stable derived market PDA.
   */
  readonly marketNonceDecimal?: string;
  /** Accepted for existing callers; mint addresses are derived from market and index. */
  readonly outcomeMintSecretKeys?: ReadonlyArray<Uint8Array>;
  /**
   * Optional frontend-create payload. When present, the orchestrator
   * posts it to `POST /v1/markets` before submitting any chain txs so
   * editorial is published only at click-Create time.
   */
  readonly editorialCreatePayload?: undefined;
}

export interface CreateMarketParamsWithEditorial extends CreateMarketParamsBase {
  /**
   * Pre-chosen market nonce (u64 decimal string). Required with
   * `editorialCreatePayload` so the backend snapshot and on-chain PDA use
   * the same seed.
   */
  readonly marketNonceDecimal: string;
  /** Accepted for existing callers; editorial mints are normalized to their PDAs. */
  readonly outcomeMintSecretKeys?: ReadonlyArray<Uint8Array>;
  /**
   * Frontend-create payload. When present, the orchestrator posts it to
   * `POST /v1/markets` before submitting any chain txs so editorial is
   * published only at click-Create time.
   */
  readonly editorialCreatePayload: MarketEditorialCreateVars;
}

export type CreateMarketParams =
  | CreateMarketParamsWithEditorial
  | CreateMarketParamsWithoutEditorial;

/** Per-stage progress emitted to the UI as the orchestrator runs. */
export interface CreateMarketProgressState {
  readonly status: 'idle' | 'preparing' | 'shell' | 'outcomes' | 'finalize' | 'done' | 'failed';
  /** Market PDA derived once the nonce is chosen — present from `shell` onward. */
  readonly marketPubkey: string | null;
  /** Signature of the `create_market` tx; null until submitted. */
  readonly shellSubmitted: string | null;
  /** True once `create_market` is confirmed. */
  readonly shellConfirmed: boolean;
  /** Number of outcomes proven prepared by local receipts or confirmed chain state. */
  readonly outcomesConfirmed: number;
  /** Total outcomes the market will have (= `params.outcomes.length`). */
  readonly outcomesTotal: number;
  /** Signature of the final outcome preparation; null until confirmed. */
  readonly finalizeSubmitted: string | null;
  /** True once all declared outcomes are prepared (terminal success). */
  readonly finalizeConfirmed: boolean;
  /** Last error if `status === 'failed'`. */
  readonly error: { stage: CreateMarketProgressState['status']; message: string } | null;
}

export interface CreateMarketResult {
  readonly marketPubkey: string;
  readonly shellSignature: string;
  readonly outcomeSignatures: ReadonlyArray<string>;
  /** Last local preparation signature, or null when recovery prepared every outcome. */
  readonly finalizeSignature: string | null;
  /** Outcome-mint pubkeys (base58) — useful for the success UI / share links. */
  readonly outcomeMintsBase58: ReadonlyArray<string>;
}

export const INITIAL_PROGRESS_STATE: CreateMarketProgressState = {
  status: 'idle',
  marketPubkey: null,
  shellSubmitted: null,
  shellConfirmed: false,
  outcomesConfirmed: 0,
  outcomesTotal: 0,
  finalizeSubmitted: null,
  finalizeConfirmed: false,
  error: null,
};
